import React, { useState } from "react";
import { useLanguage } from "../../LanguageContext";
import translations from "../../translation_skills";
import SectionHeader from "../shared/SectionHeader";
import {
  PROGRAMMING_LANGUAGES,
  LIBRARIES_AND_FRAMEWORKS,
  TOOLS_AND_UTILITIES,
} from "../../data/skills";

function Skills() {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeTab, setActiveTab] = useState("programming");

  const iconStyle = "h-6 w-6 text-gray-400 hover:text-cyan-400 transition-colors duration-300 group-hover:scale-110 transform transition-transform";

  return (
    <>
      <section
        id="skills"
        className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24"
        aria-label="Skills"
      >
        {/* HUD Header */}
        {/* <div className="sticky top-0 z-20 -mx-6 mb-8 w-screen bg-[#021327]/90 px-6 py-5 backdrop-blur border-b border-cyan-500/20 md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
          <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-400 lg:sr-only flex items-center gap-2">
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
            {t.nav}
          </h2>
        </div> */}

        <div className="relative">
          <SectionHeader label="TECH.STACK" />

          <div className="space-y-8">
            {/* Tab Navigation */}
            <div className="mb-8">
              <div className="relative flex rounded-lg bg-gray-900/50 p-1 border border-cyan-400/20">
                <div
                  className={`absolute inset-y-1 w-1/2 bg-cyan-400/20 rounded-md transition-transform duration-300 ${
                    activeTab === "softSkills" ? "translate-x-full" : "translate-x-0"
                  }`}
                ></div>
                <button
                  className={`relative z-10 flex-1 px-4 py-2 font-mono font-semibold text-center transition-colors duration-300 text-sm ${
                    activeTab === "programming" ? "text-cyan-400" : "text-gray-400 hover:text-gray-300"
                  }`}
                  onClick={() => setActiveTab("programming")}
                >
                  PROGRAMMING
                </button>
                <button
                  className={`relative z-10 flex-1 px-4 py-2 font-mono font-semibold text-center transition-colors duration-300 text-sm ${
                    activeTab === "softSkills" ? "text-cyan-400" : "text-gray-400 hover:text-gray-300"
                  }`}
                  onClick={() => setActiveTab("softSkills")}
                >
                  {t.softskills_title.toUpperCase()}
                </button>
              </div>

              {/* Tab Content */}
              <div className="mt-4">
                {activeTab === "programming" && (
                  <ul className="flex flex-wrap gap-2" aria-label="Technologies used:">
                    {PROGRAMMING_LANGUAGES.map((lang) => (
                      <li key={lang} className="group">
                        <div className="flex items-center rounded-lg bg-cyan-400/5 border border-cyan-400/20 px-3 py-2 text-xs font-mono font-medium leading-5 text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-all duration-300 cursor-default">
                          <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2 opacity-60 group-hover:opacity-100"></span>
                          {lang}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                
                {activeTab === "softSkills" && (
                  <ul className="flex flex-wrap gap-2" aria-label="Soft Skills:">
                    {t.softskills.map((skill) => (
                      <li key={skill} className="group">
                        <div className="flex items-center rounded-lg bg-cyan-400/5 border border-cyan-400/20 px-3 py-2 text-xs font-mono font-medium leading-5 text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-all duration-300 cursor-default">
                          <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2 opacity-60 group-hover:opacity-100"></span>
                          {skill}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Libraries & Frameworks */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-400 font-mono flex items-center">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 opacity-60"></span>
                LIBRARIES & FRAMEWORKS
              </h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4">
                {LIBRARIES_AND_FRAMEWORKS.map(({ Icon, name }) => (
                  <div key={name} className="group relative flex flex-col items-center p-3 rounded-lg bg-gray-900/30 border border-cyan-400/10 hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-all duration-300">
                    <Icon className={iconStyle} />
                    <span className="text-[8.5px] text-gray-500 mt-2 font-mono group-hover:text-cyan-400 transition-colors duration-300">{name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tools & Utilities */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-400 font-mono flex items-center">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 opacity-60"></span>
                TOOLS & UTILITIES
              </h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4">
                {TOOLS_AND_UTILITIES.map(({ Icon, name }) => (
                  <div key={name} className="group relative flex flex-col items-center p-3 rounded-lg bg-gray-900/30 border border-cyan-400/10 hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-all duration-300">
                    <Icon className={iconStyle} />
                    <span className="text-[8.5px] text-gray-500 mt-2 font-mono group-hover:text-cyan-400 transition-colors duration-300">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default React.memo(Skills);